import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useSupabaseTable } from './useSupabaseTable';
import { buildSupabaseFilters, getRoleFilterInfo } from '../utils/roleFilters';
import { readCache, writeCache } from '../utils/cacheManager';

function useChannelCampaignLeads(campaignType: string, cacheKey: string, mapRow: (row: any) => any) {
  const [data, setData] = useState<any[]>(() => readCache(cacheKey) || []);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const initialFetchDoneRef = useRef(false);

  const fetchData = useCallback(async (isBackgroundUpdate = false) => {
    try {
      if (!isBackgroundUpdate) setIsLoading(true);
      setError(null);

      const roleInfo = getRoleFilterInfo();
      if (!roleInfo) {
        setData([]);
        setIsLoading(false);
        initialFetchDoneRef.current = true;
        return;
      }

      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'get_channel_campaign_leads',
        {
          p_client_id: roleInfo.clientId,
          p_campaign_type: campaignType,
          p_user_id: roleInfo.isAdmin ? null : roleInfo.userId,
        }
      );

      if (rpcError) throw rpcError;

      const mappedData = (rpcData || []).map(mapRow);

      if (isMountedRef.current) {
        setData(mappedData);
        setIsLoading(false);
        initialFetchDoneRef.current = true;
        writeCache(cacheKey, mappedData);
      }
    } catch (err) {
      console.error(`[use${campaignType}CampaignLeads] Error:`, err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        if (!initialFetchDoneRef.current) setIsLoading(false);
      }
    }
  }, [campaignType, cacheKey, mapRow]);

  const debouncedFetch = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => { fetchData(true); }, 500);
  }, [fetchData]);

  useEffect(() => {
    isMountedRef.current = true;
    const roleInfo = getRoleFilterInfo();
    const hasCache = data.length > 0;
    if (hasCache) {
      setIsLoading(false);
      fetchData(true);
    } else {
      fetchData(false);
    }

    const leadsChannel = supabase
      .channel(`${campaignType}_campaign_leads_tracking_changes`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads_tracking', filter: roleInfo?.clientId ? `client_id=eq.${roleInfo.clientId}` : undefined }, () => { debouncedFetch(); })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads_campaigns', filter: roleInfo?.clientId ? `client_id=eq.${roleInfo.clientId}` : undefined }, () => { debouncedFetch(); })
      .subscribe();

    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      supabase.removeChannel(leadsChannel);
    };
  }, []);

  return { data, isLoading, error, refetch: fetchData };
}

const mapCallRow = (row: any) => ({
  ...row,
  call_attempts: Number(row.call_attempts) || 0,
  pickups: Number(row.connected_calls) || 0,
  lead_status: row.ca_lead_status || 'Assigned',
  lead_temprature: row.ca_lead_temp,
  last_call_outcome: row.ca_last_call_outcome,
  next_call: row.ca_next_call_timestamp,
  last_call_transcript: row.ca_last_call_transcript,
  ovrall_calls_convo_summary: row.ca_ovrall_calls_convo_summary,
  calls_convo_history_summarized: row.ca_calls_convo_history_summarized,
  has_transcripts: row.has_transcripts || false,
  has_recordings: row.has_recordings || false,
});

const mapWhatsAppRow = (row: any) => ({
  ...row,
  messages_sent: Number(row.messages_sent) || 0,
  replies_received: Number(row.replies_received) || 0,
  lead_status: row.wa_lead_status || 'Assigned',
  lead_temprature: row.wa_lead_temp,
  next_fu_datetime: row.wa_next_fu_datetime,
  conversation_thread: row.wa_conversation_thread,
  convo_summary: row.wa_convo_summary,
  whatsapp_thread: row.wa_conversation_thread,
});

const mapEmailRow = (row: any) => ({
  ...row,
  emails_sent: Number(row.messages_sent) || 0,
  replies_received: Number(row.replies_received) || 0,
  lead_status: row.ea_lead_status || 'Assigned',
  lead_temprature: row.ea_lead_temp,
  next_fu_datetime: row.ea_next_fu_datetime,
  email_thread: row.ea_email_thread,
  convo_summary: row.ea_convo_summary,
});

const mapLinkedInRow = (row: any) => ({
  ...row,
  messages_sent: Number(row.messages_sent) || 0,
  replies_received: Number(row.replies_received) || 0,
  lead_status: row.la_lead_status || 'Assigned',
  lead_temprature: row.la_lead_temp,
  next_fu_datetime: row.la_next_fu_datetime,
  conversation_thread: row.la_conversation_thread,
  convo_summary: row.la_convo_summary,
  cr_sent: row.la_cr_sent,
  cr_accepted: row.la_cr_accepted,
  la_connection_status: row.la_connection_status,
});

export function useCallCampaignLeads() {
  return useChannelCampaignLeads('call', 'call_campaign_leads', mapCallRow);
}

export function useWhatsAppCampaignLeads() {
  return useChannelCampaignLeads('whatsapp', 'whatsapp_campaign_leads', mapWhatsAppRow);
}

export function useEmailCampaignLeads() {
  return useChannelCampaignLeads('email', 'email_campaign_leads', mapEmailRow);
}

export function useLinkedInCampaignLeads() {
  return useChannelCampaignLeads('linkedin', 'linkedin_campaign_leads', mapLinkedInRow);
}

export function useOmniCampaignLeads() {
  const [data, setData] = useState<any[]>(() => readCache('omni_campaign_leads') || []);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const initialFetchDoneRef = useRef(false);

  const fetchData = useCallback(async (isBackgroundUpdate = false) => {
    try {
      if (!isBackgroundUpdate) {
        setIsLoading(true);
      }
      setError(null);

      const roleInfo = getRoleFilterInfo();
      if (!roleInfo) {
        setData([]);
        setIsLoading(false);
        initialFetchDoneRef.current = true;
        return;
      }

      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'get_campaign_leads_with_step_progress',
        {
          p_client_id: roleInfo.clientId,
          p_campaign_type: 'omni',
          p_user_id: roleInfo.isAdmin ? null : roleInfo.userId,
        }
      );

      if (rpcError) throw rpcError;

      const mappedData = (rpcData || []).map((row: any) => ({
        ...row,
        lead_status: row.ovrall_lead_status || 'Assigned',
        lead_temperature: row.ovrall_lead_temp,
        next_follow_up: row.next_follow_up || null,
      }));

      if (isMountedRef.current) {
        setData(mappedData);
        setIsLoading(false);
        initialFetchDoneRef.current = true;
        writeCache('omni_campaign_leads', mappedData);
      }
    } catch (err) {
      console.error('[useOmniCampaignLeads] Error fetching data:', err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        if (!initialFetchDoneRef.current) {
          setIsLoading(false);
        }
      }
    }
  }, []);

  const debouncedFetch = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      fetchData(true);
    }, 500);
  }, [fetchData]);

  useEffect(() => {
    isMountedRef.current = true;
    const roleInfo = getRoleFilterInfo();
    const hasCache = data.length > 0;
    if (hasCache) {
      setIsLoading(false);
      fetchData(true);
    } else {
      fetchData(false);
    }

    const leadsChannel = supabase
      .channel('omni_campaign_leads_tracking_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads_tracking', filter: roleInfo?.clientId ? `client_id=eq.${roleInfo.clientId}` : undefined }, () => { debouncedFetch(); })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads_campaigns', filter: roleInfo?.clientId ? `client_id=eq.${roleInfo.clientId}` : undefined }, () => { debouncedFetch(); })
      .subscribe();

    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      supabase.removeChannel(leadsChannel);
    };
  }, []);

  return { data, isLoading, error, refetch: fetchData };
}

export function useInboundCampaignLeads() {
  const [data, setData] = useState<any[]>(() => readCache('inbound_campaign_leads') || []);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const initialFetchDoneRef = useRef(false);

  const fetchData = useCallback(async (isBackgroundUpdate = false) => {
    try {
      if (!isBackgroundUpdate) {
        setIsLoading(true);
      }
      setError(null);

      const roleInfo = getRoleFilterInfo();
      if (!roleInfo) {
        setData([]);
        setIsLoading(false);
        initialFetchDoneRef.current = true;
        return;
      }

      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'get_campaign_leads_with_step_progress',
        {
          p_client_id: roleInfo.clientId,
          p_campaign_type: 'inbound',
          p_user_id: roleInfo.isAdmin ? null : roleInfo.userId,
        }
      );

      if (rpcError) throw rpcError;

      const mappedData = (rpcData || []).map((row: any) => ({
        ...row,
        lead_status: row.ovrall_lead_status || 'Assigned',
        lead_temperature: row.ovrall_lead_temp,
        next_follow_up: row.next_follow_up || null,
        total_steps: row.total_steps || (row.inbound_type === 'non_urgent' ? 17 : 11),
      }));

      if (isMountedRef.current) {
        setData(mappedData);
        setIsLoading(false);
        initialFetchDoneRef.current = true;
        writeCache('inbound_campaign_leads', mappedData);
      }
    } catch (err) {
      console.error('[useInboundCampaignLeads] Error fetching data:', err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        if (!initialFetchDoneRef.current) {
          setIsLoading(false);
        }
      }
    }
  }, []);

  const debouncedFetch = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      fetchData(true);
    }, 500);
  }, [fetchData]);

  useEffect(() => {
    isMountedRef.current = true;
    const roleInfo = getRoleFilterInfo();
    const hasCache = data.length > 0;
    if (hasCache) {
      setIsLoading(false);
      fetchData(true);
    } else {
      fetchData(false);
    }

    const leadsChannel = supabase
      .channel('inbound_campaign_leads_tracking_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads_tracking', filter: roleInfo?.clientId ? `client_id=eq.${roleInfo.clientId}` : undefined }, () => { debouncedFetch(); })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads_campaigns', filter: roleInfo?.clientId ? `client_id=eq.${roleInfo.clientId}` : undefined }, () => { debouncedFetch(); })
      .subscribe();

    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      supabase.removeChannel(leadsChannel);
    };
  }, []);

  return { data, isLoading, error, refetch: fetchData };
}

export function useInboundLeadsBySource() {
  const [data, setData] = useState<any[]>(() => readCache('inbound_source_leads') || []);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const initialFetchDoneRef = useRef(false);

  const fetchData = useCallback(async (isBackgroundUpdate = false) => {
    try {
      if (!isBackgroundUpdate) {
        setIsLoading(true);
      }
      setError(null);

      const roleInfo = getRoleFilterInfo();
      if (!roleInfo) {
        setData([]);
        setIsLoading(false);
        initialFetchDoneRef.current = true;
        return;
      }

      let leadsQuery = supabase
        .from('leads_tracking')
        .select('*')
        .eq('lead_source', 'inbound')
        .eq('client_id', roleInfo.clientId);
      if (!roleInfo.isAdmin) leadsQuery = leadsQuery.eq('user_id', roleInfo.userId);
      leadsQuery = leadsQuery.order('updated_at', { ascending: false });

      const { data: leadsData, error: leadsError } = await leadsQuery;

      if (leadsError) throw leadsError;

      const leadIds = (leadsData || []).map(l => l.lead_id).filter(Boolean);

      let campaignMap = new Map<string, any>();
      if (leadIds.length > 0) {
        const { data: campaignLeads } = await supabase
          .from('leads_campaigns')
          .select('lead_id, campaign_step, agent, status, enrollment_time, campaign_type')
          .in('lead_id', leadIds)
          .eq('client_id', roleInfo.clientId);

        for (const cl of campaignLeads || []) {
          if (cl.lead_id) campaignMap.set(cl.lead_id, cl);
        }
      }

      const mappedData = (leadsData || []).map(lead => {
        const campaignInfo = campaignMap.get(lead.lead_id) || {};
        return {
          ...lead,
          campaign_step: campaignInfo.campaign_step,
          current_agent: campaignInfo.agent,
          campaign_status: campaignInfo.status,
          campaign_type: campaignInfo.campaign_type,
          enrollment_time: campaignInfo.enrollment_time,
          lead_status: lead.ovrall_lead_status || 'Assigned',
          lead_temperature: lead.ovrall_lead_temp,
        };
      });

      if (isMountedRef.current) {
        setData(mappedData);
        setIsLoading(false);
        initialFetchDoneRef.current = true;
        writeCache('inbound_source_leads', mappedData);
      }
    } catch (err) {
      console.error('[useInboundLeadsBySource] Error fetching data:', err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        if (!initialFetchDoneRef.current) {
          setIsLoading(false);
        }
      }
    }
  }, []);

  const debouncedFetch = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      fetchData(true);
    }, 500);
  }, [fetchData]);

  const updateRecord = useCallback(async (leadId: string, updates: any) => {
    const { error: updateError } = await supabase
      .from('leads_tracking')
      .update(updates)
      .eq('lead_id', leadId);

    if (updateError) throw updateError;
    await fetchData(true);
  }, [fetchData]);

  useEffect(() => {
    isMountedRef.current = true;
    const hasCache = data.length > 0;
    if (hasCache) {
      setIsLoading(false);
      fetchData(true);
    } else {
      fetchData(false);
    }

    const leadsChannel = supabase
      .channel('inbound_source_leads_tracking_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads_tracking' }, () => { debouncedFetch(); })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads_campaigns' }, () => { debouncedFetch(); })
      .subscribe();

    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      supabase.removeChannel(leadsChannel);
    };
  }, []);

  return { data, isLoading, error, updateRecord, refetch: fetchData };
}

export function usePreQualifiedLeads() {
  const filters = buildSupabaseFilters();
  return useSupabaseTable('pre_qualified_leads', {
    orderBy: 'created_at',
    ascending: false,
    secondaryOrderBy: 'lead_id',
    secondaryAscending: true,
    primaryKey: 'lead_id',
    debounceMs: 1500,
    limit: 10000,
    columns: 'lead_id, company_name, company_website, company_linkedin, company_phone, industry_1, industry_2, products_services, dm_full_name, dm_title, dm_linkedin, dm_email_1, dm_mobile_1, company_qualified, dm_qualified, mobile_verified, email_verified, linkedin_verified, lead_status, cp_disq_reason, dm_disq_reason, created_at, request_id, enrichment_status, employee_headcount, company_country',
    filters,
  });
}

export function useQualifiedLeads() {
  const roleFilters = buildSupabaseFilters();
  return useSupabaseTable('leads_tracking', {
    orderBy: 'created_at',
    ascending: true,
    secondaryOrderBy: 'lead_id',
    secondaryAscending: true,
    limit: 6000,
    columns: 'lead_id, company_name, company_phone, company_website, company_linkedin, company_country, industry_1, dm_full_name, dm_title, dm_mobile_1, dm_email_1, dm_linkedin, ovrall_lead_status, sent_to_ca, sent_to_wa, sent_to_ea, sent_to_la, meeting_booked, lga_lead_status, lead_source, user_id, client_id, created_at, updated_at, ca_call_attempts, ca_call_answered, ca_lead_temp, ca_lead_status, wa_messages_sent, wa_replies_received, wa_lead_temp, wa_lead_status, wa_last_reply_timestamp, ea_emails_sent, ea_replies_received, ea_lead_temp, ea_lead_status, ea_last_reply_timestamp, la_messages_sent, la_replies_received, la_lead_temp, la_lead_status, la_last_reply_timestamp, la_cr_sent',
    filters: [
      { column: 'lga_lead_status', operator: 'ilike', value: 'Ready To Go' },
      { column: 'lead_source', operator: 'neq', value: 'inbound' },
      ...roleFilters,
    ],
    primaryKey: 'lead_id'
  });
}

export function useCallAgentLeads() {
  const filters = buildSupabaseFilters();
  return useSupabaseTable('leads_tracking', {
    orderBy: 'updated_at',
    ascending: false,
    primaryKey: 'lead_id',
    limit: 6000,
    columns: 'lead_id, company_name, dm_full_name, ca_next_call_timestamp, ca_lead_temp, ovrall_lead_status, sent_to_ca, created_at, updated_at, client_id, user_id',
    filters,
  });
}

export function useWhatsAppAgentLeads() {
  const filters = buildSupabaseFilters();
  return useSupabaseTable('leads_tracking', {
    orderBy: 'updated_at',
    ascending: false,
    primaryKey: 'lead_id',
    limit: 6000,
    columns: 'lead_id, company_name, dm_full_name, wa_next_fu_datetime, wa_lead_temp, ovrall_lead_status, sent_to_wa, created_at, updated_at, client_id, user_id',
    filters,
  });
}

export function useEmailAgentLeads() {
  const filters = buildSupabaseFilters();
  return useSupabaseTable('leads_tracking', {
    orderBy: 'updated_at',
    ascending: false,
    primaryKey: 'lead_id',
    limit: 6000,
    columns: 'lead_id, company_name, dm_full_name, ea_next_fu_datetime, ea_lead_temp, ovrall_lead_status, sent_to_ea, created_at, updated_at, client_id, user_id',
    filters,
  });
}

export function useLinkedInAgentLeads() {
  const filters = buildSupabaseFilters();
  return useSupabaseTable('leads_tracking', {
    orderBy: 'updated_at',
    ascending: false,
    primaryKey: 'lead_id',
    limit: 6000,
    columns: 'lead_id, company_name, dm_full_name, la_next_fu_datetime, la_lead_temp, la_cr_sent_at, la_cr_accepted_at, la_connection_status, ovrall_lead_status, sent_to_la, created_at, updated_at, client_id, user_id',
    filters,
  });
}

export function useMeetings() {
  const [data, setData] = useState<any[]>(() => readCache('meetings_booked') || []);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const initialFetchDoneRef = useRef(false);

  const fetchData = useCallback(async (isBackgroundUpdate = false) => {
    try {
      if (!isBackgroundUpdate) {
        setIsLoading(true);
      }
      setError(null);

      const roleInfo = getRoleFilterInfo();
      if (!roleInfo) {
        setData([]);
        setIsLoading(false);
        initialFetchDoneRef.current = true;
        return;
      }

      let meetingsQuery = supabase
        .from('meetings_booked')
        .select('*')
        .eq('client_id', roleInfo.clientId);
      if (!roleInfo.isAdmin) meetingsQuery = meetingsQuery.eq('user_id', roleInfo.userId);
      meetingsQuery = meetingsQuery.order('meeting_date', { ascending: false });

      const { data: meetingsRows, error: meetingsError } = await meetingsQuery;

      if (meetingsError) throw meetingsError;

      if (!meetingsRows || meetingsRows.length === 0) {
        if (isMountedRef.current) {
          setData([]);
          setIsLoading(false);
          initialFetchDoneRef.current = true;
          writeCache('meetings_booked', []);
        }
        return;
      }

      const leadIds = Array.from(new Set(
        meetingsRows.map((m: any) => m.lead_id).filter(Boolean)
      ));

      let leadsMap = new Map<string, any>();
      if (leadIds.length > 0) {
        const { data: leadsData, error: leadsError } = await supabase
          .from('leads_tracking')
          .select('lead_id, company_name, company_website, company_linkedin, company_country, employee_headcount, industry_1, industry_2, products_services, company_type, dm_full_name, dm_title, dm_mobile_1, dm_email_1, dm_linkedin, ovrall_convo_summary')
          .in('lead_id', leadIds);

        if (leadsError) {
          console.error('[useMeetings] Error fetching leads:', leadsError);
        } else if (leadsData) {
          for (const lead of leadsData) {
            leadsMap.set(lead.lead_id, lead);
          }
        }
      }

      const merged = meetingsRows.map((meeting: any) => {
        const lead = leadsMap.get(meeting.lead_id) || {};
        return { ...meeting, ...lead };
      });

      if (isMountedRef.current) {
        setData(merged);
        setIsLoading(false);
        initialFetchDoneRef.current = true;
        writeCache('meetings_booked', merged);
      }
    } catch (err) {
      console.error('[useMeetings] Error fetching data:', err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        if (!initialFetchDoneRef.current) {
          setIsLoading(false);
        }
      }
    }
  }, []);

  const debouncedFetch = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      fetchData(true);
    }, 500);
  }, [fetchData]);

  useEffect(() => {
    isMountedRef.current = true;
    const hasCache = data.length > 0;
    if (hasCache) {
      setIsLoading(false);
      fetchData(true);
    } else {
      fetchData(false);
    }

    const channel = supabase
      .channel('meetings_booked_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'meetings_booked' }, () => debouncedFetch())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads_tracking' }, () => debouncedFetch())
      .subscribe();

    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      supabase.removeChannel(channel);
    };
  }, []);

  return { data, isLoading, error, refetch: fetchData };
}

export function usePipelineLeads() {
  const roleFilters = buildSupabaseFilters();
  return useSupabaseTable('leads_tracking', {
    orderBy: 'updated_at',
    ascending: false,
    secondaryOrderBy: 'lead_id',
    secondaryAscending: true,
    filters: [
      { column: 'deal_created', operator: 'eq', value: 'yes' },
      ...roleFilters,
    ],
    primaryKey: 'lead_id'
  });
}

export function useLeadTracking() {
  const filters = buildSupabaseFilters();
  return useSupabaseTable('leads_tracking', {
    orderBy: 'updated_at',
    ascending: false,
    primaryKey: 'lead_id',
    filters,
  });
}

export function useCallLogs() {
  const filters = buildSupabaseFilters();
  return useSupabaseTable('call_logs', {
    orderBy: 'created_at',
    ascending: false,
    primaryKey: 'call_id_internal',
    columns: 'call_id_internal, lead_id, call_outcome, created_at, client_id, user_id',
    filters,
  });
}

export function useWhatsAppLogs() {
  const filters = buildSupabaseFilters();
  return useSupabaseTable('leads_tracking', {
    orderBy: 'created_at',
    ascending: false,
    primaryKey: 'lead_id',
    filters,
  });
}

export function useEmailLogs() {
  const filters = buildSupabaseFilters();
  return useSupabaseTable('leads_tracking', {
    orderBy: 'created_at',
    ascending: false,
    primaryKey: 'lead_id',
    filters,
  });
}

export function useLinkedInLogs() {
  const filters = buildSupabaseFilters();
  return useSupabaseTable('leads_tracking', {
    orderBy: 'created_at',
    ascending: false,
    primaryKey: 'lead_id',
    filters,
  });
}

export function useWhatsAppMessageLogs() {
  const filters = buildSupabaseFilters();
  return useSupabaseTable('whatsapp_logs', {
    orderBy: 'created_at',
    ascending: false,
    primaryKey: 'wa_message_id_internal',
    columns: 'wa_message_id_internal, lead_id, from_role, status, created_at',
    filters,
  });
}

export function useEmailMessageLogs() {
  const filters = buildSupabaseFilters();
  return useSupabaseTable('email_logs', {
    orderBy: 'created_at',
    ascending: false,
    primaryKey: 'email_id_internal',
    columns: 'email_id_internal, lead_id, from_role, status, created_at',
    filters,
  });
}

export function useLinkedInMessageLogs() {
  const filters = buildSupabaseFilters();
  return useSupabaseTable('linkedin_logs', {
    orderBy: 'created_at',
    ascending: false,
    primaryKey: 'lkdin_message_id_internal',
    columns: 'lkdin_message_id_internal, lead_id, from_role, status, created_at',
    filters,
  });
}

export function useLinkedInUsage() {
  const filters = buildSupabaseFilters();
  return useSupabaseTable('linkedin_usage', {
    orderBy: 'date',
    ascending: false,
    primaryKey: 'id',
    columns: 'id, date, invites_sent, messages_sent, client_id',
    filters,
  });
}

export function useMeetingsBooked() {
  const filters = buildSupabaseFilters();
  return useSupabaseTable('meetings_booked', {
    orderBy: 'created_at',
    ascending: false,
    primaryKey: 'meeting_id',
    columns: 'meeting_id, lead_id, booked_channel, meeting_status, created_at',
    filters,
  });
}

export function useLeadRequests() {
  const filters = buildSupabaseFilters();
  const result = useSupabaseTable('leads_requests', {
    orderBy: 'created_at',
    ascending: false,
    primaryKey: 'request_id',
    secondaryOrderBy: 'request_id',
    secondaryAscending: false,
    filters,
  });

  return {
    ...result,
    loading: result.isLoading,
  };
}
